import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { supabase } from '../../../lib/supabase';
import { toast } from 'sonner';
import { GraduationCap, RefreshCw, Search, Users, Wallet } from 'lucide-react';
import { format } from 'date-fns';

interface BootcampSummary {
  total: number;
  by_plan: Record<string, number>;
  by_batch: Record<string, number>;
  total_revenue: number;
}

function formatIDR(n: number): string {
  return new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', maximumFractionDigits: 0 }).format(n || 0);
}

export function BootcampEnrollments({ apiPrefix = 'admin' }: { apiPrefix?: 'admin' | 'developer' }) {
  const [enrollments, setEnrollments] = useState<any[]>([]);
  const [summary, setSummary] = useState<BootcampSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  // @ts-ignore
  const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

  const fetchEnrollments = async () => {
    try {
      setLoading(true);
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${API_URL}/api/${apiPrefix}/bootcamp-enrollments`, {
        headers: {
          'Authorization': `Bearer ${session?.access_token}`
        }
      });
      const data = await res.json();
      if (data.ok) {
        setEnrollments(data.data.enrollments || []);
        setSummary(data.data.summary || null);
      } else {
        toast.error(data.error || 'Gagal memuat data pendaftar bootcamp');
      }
    } catch (err: any) {
      toast.error(err.message || 'Gagal memuat data pendaftar bootcamp');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEnrollments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = enrollments.filter((e) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (e.user?.full_name || '').toLowerCase().includes(q) ||
      (e.user?.email || '').toLowerCase().includes(q) ||
      (e.plan_id || '').toLowerCase().includes(q)
    );
  });

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2">
              <GraduationCap className="h-5 w-5" />
              Pendaftar Bootcamp
            </CardTitle>
            <CardDescription>Daftar peserta + ringkasan jumlah per paket</CardDescription>
          </div>
          <Button onClick={fetchEnrollments} variant="outline" size="sm" disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Summary */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="rounded-xl border p-4">
            <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
              <Users className="h-3.5 w-3.5" />
              Total Peserta
            </div>
            <p className="text-2xl font-bold">{summary?.total ?? (loading ? '…' : 0)}</p>
          </div>
          <div className="rounded-xl border p-4">
            <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
              <Wallet className="h-3.5 w-3.5" />
              Total Revenue
            </div>
            <p className="text-2xl font-bold">{formatIDR(summary?.total_revenue || 0)}</p>
          </div>
          <div className="rounded-xl border p-4 col-span-2">
            <p className="text-xs text-muted-foreground mb-1">Per Paket</p>
            <div className="flex flex-wrap gap-1.5">
              {summary && Object.keys(summary.by_plan).length > 0 ? (
                Object.entries(summary.by_plan).map(([plan, count]) => (
                  <span key={plan} className="rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium">
                    {plan}: <span className="font-bold">{count}</span>
                  </span>
                ))
              ) : (
                <span className="text-xs text-muted-foreground">{loading ? 'Memuat…' : 'Belum ada data'}</span>
              )}
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="relative max-w-sm">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Cari nama / email / paket…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8"
          />
        </div>

        {/* Table */}
        <div className="rounded-xl border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nama</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>WhatsApp</TableHead>
                <TableHead>Paket</TableHead>
                <TableHead>Harga</TableHead>
                <TableHead>Batch</TableHead>
                <TableHead>Tgl Daftar</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-muted-foreground">Memuat…</TableCell>
                </TableRow>
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-muted-foreground">Belum ada pendaftar</TableCell>
                </TableRow>
              ) : (
                filtered.map((e) => (
                  <TableRow key={e.id}>
                    <TableCell className="font-medium">{e.user?.full_name || '-'}</TableCell>
                    <TableCell>{e.user?.email || '-'}</TableCell>
                    <TableCell>{e.user?.whatsapp_number || '-'}</TableCell>
                    <TableCell>{e.plan_id || '-'}</TableCell>
                    <TableCell>{formatIDR(Number(e.price || 0))}</TableCell>
                    <TableCell>{e.batch || '-'}</TableCell>
                    <TableCell>
                      {e.enrolled_at ? format(new Date(e.enrolled_at), 'dd MMM yyyy HH:mm') : '-'}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
